import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, BellOff, Check, Clock, AlertTriangle, DoorOpen, X } from "lucide-react";
import { toast } from "sonner";

const WINDOWS = [
  { key: "d7", label: "7 days" },
  { key: "d3", label: "3 days" },
  { key: "d24h", label: "24 hours" },
  { key: "sameday", label: "Same day" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Shared entry-alerts panel used by desktop + mobile Deadlines pages.
export default function AlertsPanel({ m = false }) {
  const nav = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);

  const theme = m
    ? { card: "bg-[#161B22] border-white/10", title: "text-white", sub: "text-slate-400", chipOn: "bg-[#C8000F] text-white border-[#C8000F]", chipOff: "bg-white/5 text-slate-300 border-white/10", item: "bg-white/5 border-white/10" }
    : { card: "bg-white border-[#E4E7EB]", title: "text-[#14181F]", sub: "text-slate-500", chipOn: "bg-[#C8000F] text-white border-[#C8000F]", chipOff: "bg-[#F7F8FA] text-slate-600 border-[#E4E7EB]", item: "bg-white border-[#E4E7EB]" };

  const loadNotifs = useCallback(() => {
    api.get("/notifications").then((r) => { setNotifs(r.data.notifications || []); setUnread(r.data.unread || 0); }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/alert-prefs").then((r) => setPrefs(r.data)).catch(() => setPrefs(null));
    loadNotifs();
  }, [loadNotifs]);

  const savePrefs = (next) => {
    setPrefs(next);
    api.put("/alert-prefs", next).then(() => loadNotifs()).catch(() => toast.error("Could not save alert settings"));
  };
  const toggle = (key) => prefs && savePrefs({ ...prefs, [key]: !prefs[key] });

  const markRead = (id) => {
    api.post("/notifications/read", id ? { id } : {}).then(() => loadNotifs()).catch(() => {});
    window.dispatchEvent(new Event("rp-notifs-read"));
  };
  const dismiss = (e, id) => {
    e.stopPropagation();
    setNotifs((n) => n.filter((x) => x.id !== id));
    api.delete(`/notifications/${id}`).then(() => loadNotifs()).catch(() => {});
    window.dispatchEvent(new Event("rp-notifs-read"));
  };
  const open = (n) => { if (!n.read) markRead(n.id); if (n.rodeo_id) nav(`/rodeo/${n.rodeo_id}?tab=entries`); };

  if (!prefs) return null;

  return (
    <div className="space-y-3" data-testid="alerts-panel">
      {/* settings */}
      <div className={`rounded-lg border ${theme.card} p-4`} data-testid="alert-settings">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {prefs.enabled ? <Bell className="w-5 h-5 text-[#C8000F]" /> : <BellOff className="w-5 h-5 text-slate-400" />}
            <div>
              <div className={`font-display font-bold text-sm ${theme.title}`}>Entry Reminders</div>
              <div className={`text-[11px] ${theme.sub}`}>For rodeos you've picked, entered or starred.</div>
            </div>
          </div>
          <button data-testid="alert-master-toggle" onClick={() => savePrefs({ ...prefs, enabled: !prefs.enabled })}
            className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${prefs.enabled ? "bg-[#C8000F]" : m ? "bg-white/15" : "bg-slate-300"}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${prefs.enabled ? "translate-x-5" : ""}`} />
          </button>
        </div>
        {prefs.enabled && (
          <div className="mt-3 space-y-3">
            <button data-testid="alert-opens-toggle" onClick={() => toggle("opens")}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border transition-colors ${prefs.opens ? theme.chipOn : theme.chipOff}`}>
              <span className="flex items-center gap-2 text-sm font-semibold"><DoorOpen className="w-4 h-4" /> Tell me when entries OPEN</span>
              {prefs.opens && <Check className="w-4 h-4" />}
            </button>
            <div>
              <div className={`text-[10px] font-bold uppercase tracking-wide mb-1.5 ${theme.sub}`}>Remind me before entries close</div>
              <div className="flex flex-wrap gap-2">
                {WINDOWS.map((w) => (
                  <button key={w.key} data-testid={`alert-window-${w.key}`} onClick={() => toggle(w.key)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${prefs[w.key] ? theme.chipOn : theme.chipOff}`}>
                    {prefs[w.key] && <Check className="w-3 h-3 inline mr-1 -mt-0.5" />}{w.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* notifications feed */}
      <div className={`rounded-lg border ${theme.card} p-4`} data-testid="alert-feed">
        <div className="flex items-center justify-between mb-2">
          <div className={`font-display font-bold text-sm ${theme.title}`}>
            Notifications {unread > 0 && <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#C8000F] text-white">{unread}</span>}
          </div>
          {unread > 0 && (
            <button data-testid="alert-mark-all-read" onClick={() => markRead(null)} className="text-xs font-semibold text-[#C8000F] hover:underline">Mark all read</button>
          )}
        </div>
        {notifs.length === 0 ? (
          <div className={`text-sm py-6 text-center ${theme.sub}`}>No alerts yet. Star a rodeo or pick a performance and we'll remind you before entries close.</div>
        ) : (
          <div className="space-y-2">
            {notifs.map((n) => {
              const isChange = n.type === "deadline_change";
              const isOpen = n.type === "entry_open";
              const IconC = isChange ? AlertTriangle : isOpen ? DoorOpen : Clock;
              const iconCls = isChange ? "bg-[#E68A2E]/15 text-[#E68A2E]" : isOpen ? "bg-[#3B6B1E]/15 text-[#3B6B1E]" : "bg-[#C8000F]/15 text-[#C8000F]";
              return (
                <div key={n.id} data-testid={`notif-${n.id}`} onClick={() => open(n)}
                  className={`group relative w-full text-left rounded-lg border px-3 py-2.5 flex items-start gap-2.5 cursor-pointer transition-colors ${theme.item} ${!n.read ? (m ? "ring-1 ring-[#C8000F]/50" : "ring-1 ring-[#C8000F]/30") : "opacity-75"}`}>
                  <span className={`mt-0.5 shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${iconCls}`}>
                    <IconC className="w-4 h-4" />
                  </span>
                  <div className="min-w-0 flex-1 pr-5">
                    <div className="flex items-center gap-2">
                      <span className={`font-semibold text-sm ${theme.title}`}>{n.title}</span>
                      {n.priority === "high" && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#C8000F] text-white uppercase">Priority</span>}
                    </div>
                    <div className={`text-xs mt-0.5 ${theme.sub}`}>{n.body}</div>
                    <div className={`text-[10px] mt-1 ${theme.sub}`}>{timeAgo(n.created_at)}</div>
                  </div>
                  <button data-testid={`notif-dismiss-${n.id}`} onClick={(e) => dismiss(e, n.id)} aria-label="Dismiss"
                    className={`absolute top-2 right-2 p-1 rounded ${m ? "text-slate-500 hover:text-white hover:bg-white/10" : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
